import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Statistic, Spin, message, Radio } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { Line, Pie } from '@ant-design/plots';
import { getMonthlyFinanceSummary } from '../../services/financeService';

const FinanceManagement = () => {
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [chartMetric, setChartMetric] = useState('orders'); // 'orders' or 'weight'

  const months = (() => {
    const result = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      result.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: `${date.getFullYear()}年${date.getMonth() + 1}月`
      });
    }
    return result;
  })();
  const [summaryData, setSummaryData] = useState({
    customers: [],
    totals: {
      total_orders: 0,
      total_amount: 0
    },
    growth: {
      revenue_growth: 0,
      order_growth: 0
    },
    monthly_orders: []
  });

  const fetchSummaryData = async (month = selectedMonth) => {
    try {
      setLoading(true);
      const data = await getMonthlyFinanceSummary(month);
      setSummaryData(data);
    } catch (error) {
      message.error('获取财务数据失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaryData();
  }, []);

  const columns = [
    {
      title: '客户',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '订单数量',
      dataIndex: 'total_orders',
      key: 'total_orders',
      sorter: (a, b) => a.total_orders - b.total_orders,
    },
    {
      title: '总金额 (¥)',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => amount.toFixed(2),
      sorter: (a, b) => a.total_amount - b.total_amount,
    },
    {
      title: '平均订单金额 (¥)',
      dataIndex: 'average_amount',
      key: 'average_amount',
      render: (amount) => amount.toFixed(2),
      sorter: (a, b) => a.average_amount - b.average_amount,
    },
    {
      title: '订单重量 (kg)',
      dataIndex: 'total_weight',
      key: 'total_weight',
      render: (weight) => weight.toFixed(2),
      sorter: (a, b) => a.total_weight - b.total_weight,
    },
    {
      title: '平均重量 (kg)',
      dataIndex: 'average_weight',
      key: 'average_weight',
      render: (weight) => weight.toFixed(2),
      sorter: (a, b) => a.average_weight - b.average_weight,
    }
  ];

  return (
    <div>
      <Spin spinning={loading}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <Radio.Group 
            value={chartMetric} 
            onChange={(e) => setChartMetric(e.target.value)}
            size="small"
          >
            <Radio.Button value="orders">订单数量</Radio.Button>
            <Radio.Button value="weight">订单重量</Radio.Button>
          </Radio.Group>
        </div>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Card bodyStyle={{ padding: '12px' }}>
              <Statistic
                title="月度总收入"
                style={{ fontSize: '12px' }}
                valueStyle={{ fontSize: '20px' }}
                value={summaryData.totals.total_amount}
                precision={2}
                prefix="¥"
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card bodyStyle={{ padding: '12px' }}>
              <Statistic
                title="月度总订单"
                style={{ fontSize: '12px' }}
                valueStyle={{ fontSize: '20px' }}
                value={summaryData.totals.total_orders}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card bodyStyle={{ padding: '12px' }}>
              <Statistic
                title="月度总重量"
                style={{ fontSize: '12px' }}
                valueStyle={{ fontSize: '20px' }}
                value={summaryData.totals.total_weight}
                precision={2}
                suffix="kg"
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card bodyStyle={{ padding: '12px' }}>
              <Statistic
                title="平均订单重量"
                style={{ fontSize: '12px' }}
                valueStyle={{ fontSize: '20px' }}
                value={summaryData.totals.total_weight / summaryData.totals.total_orders || 0}
                precision={2}
                suffix="kg"
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card bodyStyle={{ padding: '12px' }}>
              <Statistic
                title="收入增长"
                style={{ fontSize: '12px' }}
                valueStyle={{ 
                  fontSize: '20px',
                  color: summaryData.growth.revenue_growth >= 0 ? '#3f8600' : '#cf1322'
                }}
                value={summaryData.growth.revenue_growth}
                precision={1}
                prefix={
                  summaryData.growth.revenue_growth >= 0 ? (
                    <ArrowUpOutlined style={{ color: '#3f8600' }} />
                  ) : (
                    <ArrowDownOutlined style={{ color: '#cf1322' }} />
                  )
                }
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card bodyStyle={{ padding: '12px' }}>
              <Statistic
                title="订单增长"
                style={{ fontSize: '12px' }}
                valueStyle={{ 
                  fontSize: '20px',
                  color: summaryData.growth.order_growth >= 0 ? '#3f8600' : '#cf1322'
                }}
                value={summaryData.growth.order_growth}
                precision={1}
                prefix={
                  summaryData.growth.order_growth >= 0 ? (
                    <ArrowUpOutlined style={{ color: '#3f8600' }} />
                  ) : (
                    <ArrowDownOutlined style={{ color: '#cf1322' }} />
                  )
                }
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Card 
              title="订单趋势" 
              style={{ height: '400px' }}
              bodyStyle={{ padding: '12px' }}
              headStyle={{ padding: '0 12px', minHeight: '40px', fontSize: '14px' }}
            >
              <Line
                data={(summaryData.monthly_orders || [])
                  .map(order => ({
                    ...order,
                    weight: order.order_count * (
                      summaryData.customers.find(c => c.name === order.customer)?.average_weight || 0
                    )
                  }))
                  .sort((a, b) => {
                    // Sort by month chronologically
                    const [yearA, monthA] = a.month.split('-').map(Number);
                    const [yearB, monthB] = b.month.split('-').map(Number);
                    
                    if (yearA !== yearB) return yearA - yearB;
                    return monthA - monthB;
                  })}
                xField="month"
                yField={chartMetric === 'orders' ? 'order_count' : 'weight'}
                seriesField="customer"
                point={{
                  size: 5,
                  shape: 'circle',
                }}
                tooltip={{
                  customContent: (title, items) => {
                    return (
                      <div style={{ padding: '8px' }}>
                        <div style={{ marginBottom: '4px' }}>月份: {title}</div>
                        {items.map((item, index) => (
                          <div key={index} style={{ color: item.color }}>
                            {item.name}: {item.value} {chartMetric === 'orders' ? '单' : 'kg'}
                          </div>
                        ))}
                      </div>
                    );
                  }
                }}
                yAxis={{
                  title: {
                    text: chartMetric === 'orders' ? '订单数量' : '订单重量 (kg)',
                  },
                  grid: {
                    line: {
                      style: {
                        stroke: '#E5E5E5',
                      },
                    },
                  },
                  min: 0,
                }}
                xAxis={{
                  title: {
                    text: '月份',
                  },
                }}
                legend={{
                  position: 'top',
                }}
                smooth={true}
                padding="auto"
                height={320}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>客户{chartMetric === 'orders' ? '订单' : '重量'}占比</span>
                  <Radio.Group 
                    value={selectedMonth} 
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      fetchSummaryData(e.target.value);
                    }}
                    size="small"
                  >
                    {months.map(month => (
                      <Radio.Button key={month.value} value={month.value}>
                        {month.label}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </div>
              }
              style={{ height: '400px' }}
              bodyStyle={{ padding: '12px' }}
              headStyle={{ padding: '0 12px', minHeight: '40px', fontSize: '14px' }}
            >
              <div style={{ height: 320 }}>
                <Pie
                  data={summaryData.customers.map(customer => ({
                    type: customer.name,
                    value: chartMetric === 'orders' ? customer.total_orders : customer.total_weight
                  }))}
                  angleField="value"
                  colorField="type"
                  radius={0.8}
                  height={320}
                  label={{
                    type: 'outer',
                    content: '{percentage}',
                  }}
                  tooltip={{
                    formatter: (datum) => {
                      return { 
                        name: datum.type, 
                        value: `${datum.value} ${chartMetric === 'orders' ? '单' : 'kg'} (${(datum.percent * 100).toFixed(1)}%)` 
                      };
                    }
                  }}
                  legend={{
                    position: 'right',
                    layout: 'vertical',
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>

        <Card 
          title="客户订单详情" 
          style={{ marginTop: 12 }}
          bodyStyle={{ padding: '12px' }}
          headStyle={{ padding: '0 12px', minHeight: '40px', fontSize: '14px' }}
        >
          <Table
            columns={columns}
            dataSource={summaryData.customers}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      </Spin>
    </div>
  );
};

export default FinanceManagement;
